import { ArrowUpRightIcon, CaretDownIcon, CaretUpIcon, XIcon } from "@phosphor-icons/react"
import { AnimatePresence, motion } from "motion/react"
import { useMemo, type ReactNode } from "react"

import { IconButton } from "~/components/reusable/Button"
import { ItemIdentity } from "~/components/reusable/ItemIdentity"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { HOTKEYS } from "~/constants/hotkeys"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"
import { isEditableElement, isInDialog } from "~/utils/helpers"
import { useHotkey } from "@tanstack/react-hotkeys"
import {
  useRegisterInspectorContext,
  useRegisterInspectorNav,
} from "~/components/design-system/pages/internal/InspectorContext"
import { useInspectorDrilldown } from "~/components/design-system/pages/internal/InspectorDrilldownContext"
import { useInspectorHighlightStep } from "~/components/design-system/pages/internal/inspectorHighlight"
import InspectorBackButton from "~/components/design-system/pages/internal/InspectorBackButton"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"

// The inspector's own enter/exit timing, so the chrome settles with the panel, not after it.
const CHROME_TRANSITION = { duration: 0.18, ease: [0.22, 1, 0.36, 1] } as const
// The caret's own width: how far the identity travels as it opens or collapses.
const BACK_TRAVEL_PX = 24

/** The item line under a drilldown headline — "Terry Classic Bathrobe · #125093842". */
const buildIdentityLine = (title: ReactNode, subtitle: ReactNode) => {
  if (subtitle == null || subtitle === "") return title
  return (
    <>
      {title} · {subtitle}
    </>
  )
}

type InspectorNavControlsProps = {
  onPrev?: () => void
  onNext?: () => void
  /** Drops the highlight. Omit to hide the button. */
  onClear?: () => void
  /** Left of the stepper. Icon buttons only: the identity has to keep its width. */
  actions?: ReactNode
  /** Opens this item elsewhere, from an icon button beside the stepper. */
  onTitleClick?: (event: React.MouseEvent) => void
  /** Tooltip and label, e.g. "Open variant". Required for `onTitleClick` to render. */
  titleActionLabel?: string
  title: ReactNode
  subtitle?: ReactNode
  /** Drilldown level on show; takes the title line and demotes the item to the subtitle. */
  headline?: string
  /** Pops a drilldown level. Omit at the inspector's own level. */
  onBack?: () => void
  /** Title of the view `onBack` returns to. */
  backTo?: string
  /** The expand toggle, for a level that declared `expandableAt`. */
  expandSlot?: ReactNode
  imageUrl?: string | null
  /** Why stepping is unavailable, when it isn't simply the end of the list. */
  disabledReason?: string
}

/**
 * Inspector header naming the item on show, with prev/next stepping and a clear. Full width, flush
 * above the cards; the identity truncates and the buttons hold their place. It is the whole chrome
 * row: a drilldown's back caret at its head, the expand toggle beside the stepper.
 *
 * Shortcuts come from `HOTKEYS.prevRow` / `nextRow` (`k` / `j`), surfaced on hover by
 * `ShortcutTooltip`. Never bind a local key listener here.
 *
 * Approved surface: design-system/APPROVED.md.
 */
export function InspectorNavControls({
  onPrev,
  onNext,
  onClear,
  actions,
  onTitleClick,
  titleActionLabel,
  title,
  subtitle,
  headline,
  onBack,
  backTo,
  expandSlot,
  imageUrl,
  disabledReason,
}: InspectorNavControlsProps) {
  const prevDisabled = !onPrev || !!disabledReason
  const nextDisabled = !onNext || !!disabledReason
  const isCoveredByDialog = useIsCoveredByFullPageDialog()

  useHotkey(
    HOTKEYS.prevRow.hotkey,
    (event) => {
      if (isEditableElement(document.activeElement)) return
      event.preventDefault()
      if (!prevDisabled) onPrev?.()
    },
    { enabled: !prevDisabled && !isCoveredByDialog, meta: shortcutMeta("Tables", "Previous row") },
  )

  useHotkey(
    HOTKEYS.nextRow.hotkey,
    (event) => {
      if (isEditableElement(document.activeElement)) return
      event.preventDefault()
      if (!nextDisabled) onNext?.()
    },
    { enabled: !nextDisabled && !isCoveredByDialog, meta: shortcutMeta("Tables", "Next row") },
  )

  // Yields Escape to `InspectorBackButton` while a drilldown is open, so one press never both pops
  // a level and clears the item under it.
  const { isOpen: drilldownOpen } = useInspectorDrilldown()
  const { escapeHeld } = useInspectorHighlightStep()
  const clearEnabled = !!onClear && !drilldownOpen && !isCoveredByDialog && !escapeHeld
  useHotkey(
    HOTKEYS.clearHighlight.hotkey,
    (event) => {
      if (isEditableElement(document.activeElement) || isInDialog(event.target)) return
      event.preventDefault()
      onClear?.()
    },
    { enabled: clearEnabled },
  )

  const identity = (
    <ItemIdentity
      title={headline ?? title}
      subtitle={headline ? buildIdentityLine(title, subtitle) : subtitle}
      imageUrl={imageUrl}
      className="min-w-0 flex-1"
    />
  )

  return (
    // No `gap`: a flex gap is not animatable, so the caret's space rides in its own box (`pr-2`).
    <div className="flex w-full items-center">
      {/* Width, not just opacity, or the identity jumps when the level opens or closes. */}
      <AnimatePresence initial={false}>
        {onBack && (
          <motion.div
            key="back"
            className="shrink-0 overflow-hidden"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={CHROME_TRANSITION}
          >
            <div className="pr-2">
              <InspectorBackButton onBack={onBack} parentTitle={backTo} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* `popLayout` pins the outgoing copy absolutely, so both carry the caret's travel as `x`
          or the exiting copy hangs still. */}
      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={headline ?? "item"}
          className="flex min-w-0 flex-1"
          initial={{ opacity: 0, x: onBack ? BACK_TRAVEL_PX : -BACK_TRAVEL_PX }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: onBack ? -BACK_TRAVEL_PX : BACK_TRAVEL_PX }}
          transition={CHROME_TRANSITION}
        >
          {identity}
        </motion.div>
      </AnimatePresence>
      <div className="flex shrink-0 items-center gap-1 pl-2">
        {/* Not while drilled in: the row shows the LEVEL's headline, not the item's. */}
        {onTitleClick && titleActionLabel && !headline && (
          <>
            <ShortcutTooltip label={titleActionLabel} side="bottom">
              <IconButton
                variant="transparent"
                size="xs"
                aria-label={titleActionLabel}
                onClick={onTitleClick}
              >
                <ArrowUpRightIcon />
              </IconButton>
            </ShortcutTooltip>
            <div aria-hidden className="mx-1 h-4 w-px bg-border-base" />
          </>
        )}
        {actions}
        <ShortcutTooltip
          label={disabledReason || "Previous"}
          shortcut={disabledReason ? undefined : HOTKEYS.prevRow.hotkey}
          side="bottom"
        >
          <IconButton
            variant="transparent"
            size="xs"
            aria-label="Previous"
            disabled={prevDisabled}
            onClick={onPrev}
          >
            <CaretUpIcon />
          </IconButton>
        </ShortcutTooltip>
        <ShortcutTooltip
          label={disabledReason || "Next"}
          shortcut={disabledReason ? undefined : HOTKEYS.nextRow.hotkey}
          side="bottom"
        >
          <IconButton
            variant="transparent"
            size="xs"
            aria-label="Next"
            disabled={nextDisabled}
            onClick={onNext}
          >
            <CaretDownIcon />
          </IconButton>
        </ShortcutTooltip>
        {expandSlot}
        {onClear && (
          <ShortcutTooltip
            label="Clear selection"
            shortcut={clearEnabled ? HOTKEYS.clearHighlight.hotkey : undefined}
            side="bottom"
          >
            <IconButton
              variant="transparent"
              size="xs"
              aria-label="Clear selection"
              onClick={onClear}
            >
              <XIcon />
            </IconButton>
          </ShortcutTooltip>
        )}
      </div>
    </div>
  )
}

const buildSubtitle = (subtitle: ReactNode, position: string | null) => {
  if (position == null) return subtitle
  if (subtitle == null) return position
  return `${subtitle} · ${position}`
}

/**
 * Drop-in row stepper for a Module inspector, reading the stepper the table registered so it stays
 * in step with the grid's highlight. Position is appended to the caller's subtitle ("3 of 24").
 *
 * Renders where the inspector chrome pins it (above the views, through a drilldown), not here in
 * the tree; keep rendering it with the inspector's content, which knows the item on show. Clear is
 * always available, even before a table registers a stepper.
 */
const InspectorNav = ({
  title,
  subtitle,
  imageUrl,
  noun = "variants",
  actions,
  onTitleClick,
  titleActionLabel,
  below,
}: {
  title: ReactNode
  subtitle?: ReactNode
  imageUrl?: string | null
  /** See `InspectorNavControls`. */
  actions?: ReactNode
  /** See `InspectorNavControls`. */
  onTitleClick?: (event: React.MouseEvent) => void
  titleActionLabel?: string
  /** Plural noun for the position readout. */
  noun?: string
  /**
   * Context band pinned between this row and the tabs, at every level. Pass a stable node:
   * registration keys off identity.
   */
  below?: ReactNode
}) => {
  const { stepPrev, stepNext, clearHighlight, canStepPrev, canStepNext, index, total } =
    useInspectorHighlightStep()
  // Drilldown state is not read here: `ModuleInspector` injects it, as this registers the node
  // that state changes. Identity and clear always render: a persisted highlight can outlive the
  // grid's position, and a hidden header would leave no way out.
  const hasPosition = total > 0 && index >= 0
  const fullSubtitle = buildSubtitle(
    subtitle,
    hasPosition ? `${index + 1} of ${total} ${noun}` : null,
  )

  // Registered, not returned, so it survives a drilldown. Memoised: the registration effect keys
  // off node identity, so a fresh element each render would loop.
  const content = useMemo(
    () => (
      <InspectorNavControls
        onPrev={hasPosition && canStepPrev ? stepPrev : undefined}
        onNext={hasPosition && canStepNext ? stepNext : undefined}
        onClear={clearHighlight}
        title={title}
        subtitle={fullSubtitle}
        imageUrl={imageUrl}
        actions={actions}
        onTitleClick={onTitleClick}
        titleActionLabel={titleActionLabel}
      />
    ),
    [
      hasPosition,
      canStepPrev,
      canStepNext,
      stepPrev,
      stepNext,
      clearHighlight,
      title,
      fullSubtitle,
      imageUrl,
      actions,
      onTitleClick,
      titleActionLabel,
    ],
  )
  useRegisterInspectorNav(content)
  useRegisterInspectorContext(below)

  return null
}

export default InspectorNav
